/* eslint-disable max-lines, max-lines-per-function */
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  useSyncExternalStore,
} from 'react';
import PropTypes from 'prop-types';
import { TableUtility } from '@utility/dynamic-table';
import { Button } from 'primereact/button';
import { Menu } from 'primereact/menu';
import { Navigate, useNavigate } from 'react-router-dom';
import { useAuth } from '../../../Hooks/useAuth';
import { MODULE_NAMES } from '../../../Constants/modules';
import UseAxios from '../../../Api/http/http';
import {
  createAiToolMisRequest,
  deleteAiToolRequest,
  deleteAiToolRequests,
  fetchAiToolCatalog,
  fetchAiToolMisAddOptions,
  updateAiToolRequest,
} from '../../../Api/request/aiToolMis';
import { appendServiceNameToURL, appendServicePortToURL } from '../../../Api/resources/serviceURLs';
import { serviceName } from '../../../Api/resources/serviceConstants';
import { AI_TOOL_MIS } from '../../../Api/resources/endPoint';
import FormDialog from '../../../Components/Modal/modal';
import { useSnackbar } from '../../../Components/Snackbar/snackBarProvider';
import { CANCEL_TEXT } from '../../../Constants/strings';
import AIToolMISAddDialog, {
  AIToolMISMultiSelect,
  AIToolMISProjectEmployeeFields,
} from './AIToolMISAddDialog';
import alertIcon from '../../../Assets/images/svg/alert_icon.svg';
import deleteIcon from '../../../Assets/images/svg/dar_delete.svg';
import editIcon from '../../../Assets/images/svg/dar_edit.svg';
import { getExportFileName, getExportFileNameWithDate } from '../../Delivery/utils';
import './AIToolMIS.scss';

const buildApiBaseURL = () => {
  const serviceUrl = appendServiceNameToURL(serviceName.PROJECT_MS);
  if (process.env.NODE_ENV === 'production') {
    return `${process.env.REACT_APP_API_URL}${serviceUrl}/api${AI_TOOL_MIS}`;
  }
  const baseUrl = (process.env.REACT_APP_API_URL || '').replace(/\/$/, '');
  const port = appendServicePortToURL(serviceName.PROJECT_MS);
  return `${baseUrl}:${port}${serviceUrl}/api${AI_TOOL_MIS}`;
};

const baseURL = buildApiBaseURL();

const getRequestError = (response, fallback) => (
  response?.data?.response?.data?.message
  || response?.data?.message
  || response?.data?.response?.data?.error
  || fallback
);

const ensureRequestSucceeded = (response, fallback) => {
  if (response?.status < 200 || response?.status >= 300 || response?.data?.name === 'AxiosError') {
    throw new Error(getRequestError(response, fallback));
  }
  if (response?.data?.success === false) {
    throw new Error(response.data.message || fallback);
  }
  return response?.data;
};

const getSelectedToolIds = (value) => String(value || '')
  .split(',')
  .map((toolId) => Number(toolId.trim()))
  .filter((toolId) => Number.isSafeInteger(toolId) && toolId > 0);

const createRequestSelectionStore = () => {
  let selectedIds = new Set();
  const listeners = new Set();
  const notify = () => listeners.forEach((listener) => listener());
  const setSelectedIds = (nextIds) => {
    const nextSelection = new Set(nextIds);
    if (nextSelection.size === selectedIds.size
      && [...nextSelection].every((requestId) => selectedIds.has(requestId))) return;
    selectedIds = nextSelection;
    notify();
  };

  return {
    getSnapshot: () => selectedIds,
    subscribe: (listener) => {
      listeners.add(listener);
      return () => listeners.delete(listener);
    },
    setSelectedIds,
    toggleRequest: (requestId) => {
      const nextSelection = new Set(selectedIds);
      if (nextSelection.has(requestId)) nextSelection.delete(requestId);
      else nextSelection.add(requestId);
      setSelectedIds(nextSelection);
    },
    toggleVisibleRequests: (requestIds) => {
      const allVisibleSelected = requestIds.length > 0
        && requestIds.every((requestId) => selectedIds.has(requestId));
      const nextSelection = new Set(selectedIds);
      requestIds.forEach((requestId) => {
        if (allVisibleSelected) nextSelection.delete(requestId);
        else nextSelection.add(requestId);
      });
      setSelectedIds(nextSelection);
    },
  };
};

const selectionStorePropType = PropTypes.shape({
  getSnapshot: PropTypes.func.isRequired,
  subscribe: PropTypes.func.isRequired,
  toggleRequest: PropTypes.func.isRequired,
  toggleVisibleRequests: PropTypes.func.isRequired,
});

const RequestSelectionCheckbox = ({ requestId, store }) => {
  const selectedIds = useSyncExternalStore(store.subscribe, store.getSnapshot, store.getSnapshot);
  return (
    <input
      className="ai-tool-mis-checkbox"
      type="checkbox"
      checked={selectedIds.has(requestId)}
      onClick={(event) => event.stopPropagation()}
      onChange={() => store.toggleRequest(requestId)}
      aria-label={`Select AI access request ${requestId}`}
    />
  );
};

const RequestSelectionHeader = ({ requestIds, store }) => {
  const selectedIds = useSyncExternalStore(store.subscribe, store.getSnapshot, store.getSnapshot);
  const selectedVisibleCount = requestIds.filter((requestId) => selectedIds.has(requestId)).length;
  const allVisibleSelected = requestIds.length > 0 && selectedVisibleCount === requestIds.length;

  return (
    <label className="ai-tool-mis-select-header" onClick={(event) => event.stopPropagation()}>
      <input
        type="checkbox"
        checked={allVisibleSelected}
        onChange={() => store.toggleVisibleRequests(requestIds)}
        aria-label="Select all visible AI access requests"
      />
    </label>
  );
};

const EmployeeDetailsSelectionHeader = ({ label, requestIds, store }) => (
  <div className="ai-tool-mis-employee-header">
    <RequestSelectionHeader requestIds={requestIds} store={store} />
    <span>{label}</span>
  </div>
);

EmployeeDetailsSelectionHeader.propTypes = {
  label: PropTypes.node.isRequired,
  requestIds: PropTypes.arrayOf(PropTypes.number).isRequired,
  store: selectionStorePropType,
};

const EmployeeDetailsCell = ({ requestId, value, store }) => (
  <div className="ai-tool-mis-employee-cell" onClick={(event) => event.stopPropagation()}>
    <RequestSelectionCheckbox requestId={requestId} store={store} />
    <span>{value}</span>
  </div>
);

EmployeeDetailsCell.propTypes = {
  requestId: PropTypes.number.isRequired,
  value: PropTypes.string.isRequired,
  store: selectionStorePropType,
};

const ToolCountHeader = ({ label }) => (
  <span className="ai-tool-mis-count-header">{label}</span>
);

ToolCountHeader.propTypes = {
  label: PropTypes.node.isRequired,
};

const AiToolMISRowActions = ({ row, actions, onAction }) => (
  <div className="ai-tool-mis-actions" onClick={(event) => event.stopPropagation()}>
    {actions.includes('edit') && (
      <button
        type="button"
        className="ai-tool-mis-action-btn"
        title="Edit AI access request"
        aria-label={`Edit AI access request ${row.requestId}`}
        onClick={() => onAction('edit', row)}
      >
        <img src={editIcon} alt="" aria-hidden="true" />
      </button>
    )}
    {actions.includes('delete') && (
      <div
        className="sr-delete-cell"
        onClick={(event) => event.stopPropagation()}
        title={`Delete AI access request ${row.requestId}`}
      >
        <button
          type="button"
          className="sr-delete-btn"
          aria-label={`Delete AI access request ${row.requestId}`}
          onClick={() => onAction('delete', row)}
        >
          <img
            src={deleteIcon}
            alt="Delete"
            aria-hidden="true"
            className="sr-delete-btn__icon"
          />
        </button>
      </div>
    )}
  </div>
);

AiToolMISRowActions.propTypes = {
  row: PropTypes.shape({ requestId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]) }).isRequired,
  actions: PropTypes.arrayOf(PropTypes.string).isRequired,
  onAction: PropTypes.func.isRequired,
};

RequestSelectionCheckbox.propTypes = {
  requestId: PropTypes.number.isRequired,
  store: selectionStorePropType.isRequired,
};

RequestSelectionHeader.propTypes = {
  requestIds: PropTypes.arrayOf(PropTypes.number).isRequired,
  store: selectionStorePropType.isRequired,
};

const AIToolMIS = () => {
  const { userProfile } = useAuth();
  const permissionActions = userProfile?.permissions?.[MODULE_NAMES.MIS_AI_TOOL_MIS];
  const isSuperAdmin = userProfile?.roleDetails?.some((role) => role.roleName === 'Super Admin');
  const hasPermission = useCallback((action) => (
    isSuperAdmin || (Array.isArray(permissionActions) && permissionActions.includes(action))
  ), [isSuperAdmin, permissionActions]);
  const canView = hasPermission('read');
  const canCreate = hasPermission('create');
  const canUpdate = hasPermission('update');
  const canDelete = hasPermission('delete');
  const requestUserInfo = useMemo(() => ({
    roleDetails: isSuperAdmin ? [{ roleName: 'Super Admin' }] : [],
    permissions: {
      [MODULE_NAMES.MIS_AI_TOOL_MIS]: Array.isArray(permissionActions) ? permissionActions : [],
    },
  }), [isSuperAdmin, permissionActions]);
  const navigate = useNavigate();
  const { snackbarOpen } = useSnackbar();
  const [refreshKey, setRefreshKey] = useState(0);
  const [toolCatalog, setToolCatalog] = useState([]);
  const [addOptions, setAddOptions] = useState({ employees: [], projects: [] });
  const [isCatalogLoading, setIsCatalogLoading] = useState(false);
  const [isAddOptionsLoading, setIsAddOptionsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);
  const [addForm, setAddForm] = useState({
    employeeId: '',
    projectId: '',
    aiToolIds: [],
    businessJustification: '',
  });
  const [editForm, setEditForm] = useState({
    employeeId: '',
    projectId: '',
    employeeName: '',
    employeeVertical: '',
    projectName: '',
    projectManager: '',
    businessJustification: '',
  });
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [selectedToolIds, setSelectedToolIds] = useState([]);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const selectionStore = useMemo(() => createRequestSelectionStore(), []);
  const selectedRequestIds = useSyncExternalStore(
    selectionStore.subscribe,
    selectionStore.getSnapshot,
    selectionStore.getSnapshot,
  );
  const selectedRequestIdList = [...selectedRequestIds];
  const moreMenuRef = useRef(null);
  const [isBulkDeleteOpen, setIsBulkDeleteOpen] = useState(false);

  const moreMenuItems = useMemo(() => [
    ...(canDelete ? [{
      label: 'Delete Records',
      disabled: selectedRequestIdList.length === 0 || isSaving,
      command: () => setIsBulkDeleteOpen(true),
    }] : []),
    {
      label: 'AI Access Request',
      command: () => window.open('/resource/ai-access-request', '_blank', 'noopener,noreferrer'),
    },
    {
      label: 'AI Usage Dashboard',
      command: () => window.open('/resource/ai-access-request-dashboard', '_blank', 'noopener,noreferrer'),
    },
  ], [canDelete, isSaving, selectedRequestIdList.length]);

  useEffect(() => {
    if (!canView) return undefined;
    if (!isAddDialogOpen && !selectedRequest) return undefined;
    let mounted = true;
    setIsAddOptionsLoading(true);
    UseAxios(fetchAiToolMisAddOptions(requestUserInfo))
      .then((response) => {
        const payload = ensureRequestSucceeded(response, 'Unable to load employee and project options.');
        if (mounted) setAddOptions(payload?.data || { employees: [], projects: [] });
      })
      .catch((error) => {
        if (mounted) snackbarOpen(error.message || 'Unable to load employee and project options.', 'error');
      })
      .finally(() => {
        if (mounted) setIsAddOptionsLoading(false);
      });
    return () => {
      mounted = false;
    };
  }, [canView, isAddDialogOpen, requestUserInfo, selectedRequest, snackbarOpen]);

  const tableUserInfo = useMemo(() => ({
    id: userProfile?.id || '',
    emailId: userProfile?.email || '',
    employeeId: userProfile?.employeeId || '',
    ...requestUserInfo,
  }), [requestUserInfo, userProfile]);

  const handleAddIconClick = useCallback(() => {
    if (!canCreate) return;
    setAddForm({
      employeeId: '',
      projectId: '',
      aiToolIds: [],
      businessJustification: '',
    });
    setIsAddDialogOpen(true);
  }, [canCreate]);

  const handleCellClick = useCallback(({ fieldPath, rowData, action } = {}) => {
    if (fieldPath !== 'action' || !rowData) return;
    if ((action === 'edit' && !canUpdate) || (action === 'delete' && !canDelete)) return;
    if (action === 'edit') {
      setSelectedRequest(rowData);
      setSelectedToolIds(getSelectedToolIds(rowData.aiToolIds));
      setEditForm({
        employeeId: rowData.employeeId || '',
        projectId: rowData.requestProjectId || rowData.projectId || '',
        employeeName: rowData.employeeName || '',
        employeeVertical: rowData.employeeVertical || '',
        projectName: rowData.projectName || '',
        projectManager: rowData.projectManager || '',
        businessJustification: rowData.requesterComment || '',
      });
    }
    if (action === 'delete') setDeleteTarget(rowData);
  }, [canDelete, canUpdate]);

  const handleFilteredApiResponse = useCallback((response) => {
    const tableData = response?.data?.tableData;
    if (!Array.isArray(tableData)) return;
    const actionColumn = response.data.tableConfiguration?.find((column) => column.field === 'action');
    const availableActions = (Array.isArray(actionColumn?.actions) ? actionColumn.actions : [])
      .filter((action) => (action === 'edit' && canUpdate) || (action === 'delete' && canDelete));

    const visibleRequestIds = tableData
      .map((row) => Number(row.requestId))
      .filter((requestId) => Number.isSafeInteger(requestId) && requestId > 0);
    const visibleRequestIdSet = new Set(visibleRequestIds);
    const retainedSelection = [...selectionStore.getSnapshot()]
      .filter((requestId) => visibleRequestIdSet.has(requestId));
    selectionStore.setSelectedIds(retainedSelection);

    const columnWidths = {
      employeeDetails: 180,
      projectDetails: 220,
      aiToolName: 210,
      toolCount: 130,
      action: 140,
    };
    const tableConfiguration = (response.data.tableConfiguration || [])
      .filter((column) => column.field !== 'action' || availableActions.length > 0)
      .map((column) => {
        const configuredColumn = {
          ...column,
          ...(columnWidths[column.field] ? { width: columnWidths[column.field] } : {}),
        };
        if (column.field === 'action') {
          configuredColumn.type = 'string';
          delete configuredColumn.actions;
        }
        if (column.field === 'employeeDetails') {
          configuredColumn.header = canDelete ? (
            <EmployeeDetailsSelectionHeader
              label={column.header}
              requestIds={visibleRequestIds}
              store={selectionStore}
            />
          ) : column.header;
        }
        if (column.field === 'toolCount') {
          configuredColumn.header = <ToolCountHeader label={column.header} />;
        }
        return configuredColumn;
      });
    const tableDataWithSelection = tableData.map((row) => ({
      ...row,
      ...(canDelete ? { employeeDetails: (
        <EmployeeDetailsCell
          requestId={Number(row.requestId)}
          value={row.employeeDetails}
          store={selectionStore}
        />
      ) } : {}),
      toolCount: <span className="ai-tool-mis-count-cell">{row.toolCount}</span>,
      ...(actionColumn && availableActions.length > 0 ? {
        action: (
          <AiToolMISRowActions
            row={row}
            actions={availableActions}
            onAction={(action, rowData) => handleCellClick({
              fieldPath: 'action',
              rowData,
              action,
            })}
          />
        ),
      } : {}),
    }));

    response.data = {
      ...response.data,
      tableConfiguration,
      tableData: tableDataWithSelection,
    };
  }, [canDelete, canUpdate, handleCellClick, selectionStore]);

  useEffect(() => {
    if (!canView) return undefined;
    let mounted = true;
    setIsCatalogLoading(true);
    UseAxios(fetchAiToolCatalog(requestUserInfo))
      .then((response) => {
        const payload = ensureRequestSucceeded(response, 'Unable to load the AI tool catalog.');
        if (mounted) setToolCatalog(payload?.data || []);
      })
      .catch((error) => {
        if (mounted) snackbarOpen(error.message || 'Unable to load the AI tool catalog.', 'error');
      })
      .finally(() => {
        if (mounted) setIsCatalogLoading(false);
      });
    return () => {
      mounted = false;
    };
  }, [canView, requestUserInfo, snackbarOpen]);

  const configuration = useMemo(() => ({
    generalSettings: {
      enableApiFiltering: true,
      baseURL,
      headers: {
        token: userProfile?.access_token || '',
        'x-user-info': JSON.stringify(requestUserInfo),
      },
      additionalPayloadData: { employeeId: userProfile?.employeeId || '' },
      userInfo: tableUserInfo,
      handler: {
        onCellClickHandler: handleCellClick,
        handleFilteredApiResponse,
        transformPayload: (payload) => {
          const transformedPayload = { ...payload };
          delete transformedPayload.pagination;
          return transformedPayload;
        },
      },
      handlers: {
        handleAddIconClick,
      },
      apiEndpoints: {
        getAllViews: { endpoint: '', enabled: false },
        getFilteredData: { endpoint: '', enabled: true },
        saveOrUpdateView: { endpoint: '', enabled: false },
        deleteView: { endpoint: '', enabled: false },
        download: {
          endpoint: 'export',
          enabled: true,
          filename: () => getExportFileName('AI_Tool_MIS'),
          sheetName: () => getExportFileNameWithDate('AI_Tool_MIS'),
        },
      },
      showToolBar: true,
      showPagination: false,
      showTieredMenu: true,
      showFilterOption: true,
      toolBarSettings: {
        showAddNew: canCreate,
        showFilterOption: true,
        showExpandCollapse: false,
        showDownloadExcel: true,
        downloadExcelSheetName: 'AI Tool MIS',
        showColumnCustomize: false,
        showHelp: false,
        showSearch: false,
        showSaveView: false,
        showToolBarHeading: true,
        toolBarHeading: 'AI Tool MIS',
        showReset: false,
        additionalElements: [
          <div className="ai-tool-mis-more-actions" key="ai-tool-mis-menu">
            <Menu
              ref={moreMenuRef}
              model={moreMenuItems}
              popup
              appendTo={document.body}
              className="ai-tool-mis-more-menu-popup"
            />
            <Button
              type="button"
              icon="pi pi-ellipsis-v"
              className="p-button-text ai-tool-mis-more-btn"
              onClick={(event) => moreMenuRef.current?.toggle(event)}
              aria-label="More Actions"
            />
          </div>,
        ],
      },
    },
    showFilterOption: true,
    tableSettings: {
      enableTreeTable: false,
      size: 'large',
      loaderSkeleton: { rows: 10, bodyCells: 2, columns: 4 },
      showExpandCollapse: false,
      tableScrollHeight: 'calc(100vh - 245px)',
      maxFrozenColumns: { desktop: 1, tablet: 1, mobile: 1 },
    },
  }), [
    handleCellClick,
    handleAddIconClick,
    handleFilteredApiResponse,
    canCreate,
    canDelete,
    isSaving,
    moreMenuItems,
    navigate,
    selectedRequestIdList.length,
    tableUserInfo,
    userProfile,
  ]);

  const closeEditDialog = () => {
    if (isSaving) return;
    setSelectedRequest(null);
    setSelectedToolIds([]);
  };

  const closeAddDialog = () => {
    if (isSaving) return;
    setIsAddDialogOpen(false);
  };

  const saveNewRequest = async () => {
    if (!canCreate) return;
    if (!addForm.employeeId || !addForm.projectId || addForm.aiToolIds.length === 0
      || !addForm.businessJustification.trim()) return;
    setIsSaving(true);
    try {
      const response = await UseAxios(createAiToolMisRequest({
        employeeId: addForm.employeeId,
        projectId: addForm.projectId,
        aiToolIds: addForm.aiToolIds,
        businessJustification: addForm.businessJustification.trim(),
      }, requestUserInfo));
      ensureRequestSucceeded(response, 'Unable to create the AI access request.');
      snackbarOpen('AI access request created.', 'success');
      setIsAddDialogOpen(false);
      setRefreshKey((current) => current + 1);
    } catch (error) {
      snackbarOpen(error.message || 'Unable to create the AI access request.', 'error');
    } finally {
      setIsSaving(false);
    }
  };

  const saveEdit = async () => {
    if (!canUpdate) return;
    if (!selectedRequest?.requestId || !editForm.employeeId || !editForm.projectId
      || !editForm.businessJustification.trim()) return;
    setIsSaving(true);
    try {
      const response = await UseAxios(updateAiToolRequest(
        selectedRequest.requestId,
        {
          employeeId: editForm.employeeId,
          projectId: editForm.projectId,
          aiToolIds: selectedToolIds,
          businessJustification: editForm.businessJustification.trim(),
        },
        requestUserInfo,
      ));
      ensureRequestSucceeded(response, 'Unable to update the AI access request.');
      snackbarOpen('AI access request updated.', 'success');
      setSelectedRequest(null);
      setRefreshKey((current) => current + 1);
    } catch (error) {
      snackbarOpen(error.message || 'Unable to update the AI access request.', 'error');
    } finally {
      setIsSaving(false);
    }
  };

  const confirmDelete = async () => {
    if (!canDelete) return;
    if (!deleteTarget?.requestId) return;
    setIsSaving(true);
    try {
      const response = await UseAxios(deleteAiToolRequest(deleteTarget.requestId, requestUserInfo));
      ensureRequestSucceeded(response, 'Unable to deactivate the AI access request.');
      snackbarOpen('AI access request deactivated.', 'success');
      setDeleteTarget(null);
      setRefreshKey((current) => current + 1);
    } catch (error) {
      snackbarOpen(error.message || 'Unable to deactivate the AI access request.', 'error');
    } finally {
      setIsSaving(false);
    }
  };

  const confirmBulkDelete = async () => {
    if (!canDelete) return;
    if (selectedRequestIdList.length === 0) return;
    setIsSaving(true);
    try {
      const response = await UseAxios(deleteAiToolRequests(selectedRequestIdList, requestUserInfo));
      ensureRequestSucceeded(response, 'Unable to delete the selected AI access requests.');
      snackbarOpen(`${selectedRequestIdList.length} AI access request(s) deactivated.`, 'success');
      selectionStore.setSelectedIds([]);
      setIsBulkDeleteOpen(false);
      setRefreshKey((current) => current + 1);
    } catch (error) {
      snackbarOpen(error.message || 'Unable to delete the selected AI access requests.', 'error');
    } finally {
      setIsSaving(false);
    }
  };

  if (!canView) return <Navigate to="/home" replace />;

  return (
    <section className="ai-tool-mis-page">
      <div className="ai-tool-mis-table">
        <TableUtility key={refreshKey} configuration={configuration} />
      </div>

      {canCreate && <AIToolMISAddDialog
        open={isAddDialogOpen}
        form={addForm}
        setForm={setAddForm}
        employees={addOptions.employees}
        projects={addOptions.projects}
        tools={toolCatalog}
        isLoading={isAddOptionsLoading || isCatalogLoading}
        isSaving={isSaving}
        onClose={closeAddDialog}
        onSubmit={saveNewRequest}
      />}

      {canUpdate && <FormDialog
        open={Boolean(selectedRequest)}
        title="Edit AI Access Details"
        handleClose={closeEditDialog}
        dialogPaperCss="ai-tool-mis-form-dialog-paper ai-tool-mis-edit-form-dialog-paper"
        dialogBodycontent={(
          <div className="ai-tool-mis-dialog-content">
            <div className="ai-tool-mis-add-form">
              <AIToolMISProjectEmployeeFields
                form={editForm}
                setForm={setEditForm}
                employees={addOptions.employees}
                projects={addOptions.projects}
                isSaving={isSaving || isAddOptionsLoading}
                isReadOnly
                idPrefix="ai-tool-mis-edit"
              />
              <AIToolMISMultiSelect
                tools={toolCatalog}
                selectedToolIds={selectedToolIds}
                onChange={setSelectedToolIds}
                isSaving={isSaving || isCatalogLoading}
                id="ai-tool-mis-edit-tools"
              />
              <div className="ai-tool-mis-add-field">
                <label className="ai-tool-mis-add-label" htmlFor="ai-tool-mis-edit-justification">
                  Business Justification <span>*</span>
                </label>
                <input
                  id="ai-tool-mis-edit-justification"
                  className="ai-tool-mis-field-input"
                  type="text"
                  placeholder="Enter..."
                  value={editForm.businessJustification}
                  onChange={(event) => setEditForm((current) => ({
                    ...current,
                    businessJustification: event.target.value,
                  }))}
                  disabled={isSaving}
                  required
                />
              </div>
            </div>
          </div>
        )}
        button1Action={closeEditDialog}
        button2Action={saveEdit}
        button1={CANCEL_TEXT}
        button2={isSaving ? 'Saving...' : 'Save'}
        cancelButtonId="ai-tool-mis-edit-cancel"
        submitButtonId="ai-tool-mis-edit-save"
        button2Disabled={isSaving || isCatalogLoading || isAddOptionsLoading
          || !editForm.employeeId || !editForm.projectId
          || !editForm.businessJustification.trim()}
      />}

      {canDelete && <FormDialog
        open={Boolean(deleteTarget)}
        title="Delete AI Access Request"
        handleClose={() => !isSaving && setDeleteTarget(null)}
        dialogCss="dialog-message"
        dialogPaperCss="ai-tool-mis-delete-dialog-paper"
        dialogBodycontent={(
          <div className="ai-tool-mis-delete-body">
            <div className="ai-tool-mis-delete-alert-icon">
              <img src={alertIcon} alt="" aria-hidden="true" />
            </div>
            <div className="ai-tool-mis-delete-message">
              <div>Are you sure you want to delete this record?</div>
            </div>
          </div>
        )}
        button1Action={() => !isSaving && setDeleteTarget(null)}
        button2Action={confirmDelete}
        button1={CANCEL_TEXT}
        button2={isSaving ? 'Deleting...' : 'Delete'}
        cancelButtonId="ai-tool-mis-delete-cancel"
        submitButtonId="ai-tool-mis-delete-confirm"
        button2Disabled={isSaving}
      />}

      {canDelete && <FormDialog
        open={isBulkDeleteOpen}
        title="Delete Selected AI Access Requests"
        handleClose={() => !isSaving && setIsBulkDeleteOpen(false)}
        dialogCss="dialog-message"
        dialogPaperCss="ai-tool-mis-delete-dialog-paper"
        dialogBodycontent={(
          <div className="ai-tool-mis-delete-body">
            <div className="ai-tool-mis-delete-alert-icon">
              <img src={alertIcon} alt="" aria-hidden="true" />
            </div>
            <div className="ai-tool-mis-delete-message">
              <div>Are you sure you want to delete these records?</div>
            </div>
          </div>
        )}
        button1Action={() => !isSaving && setIsBulkDeleteOpen(false)}
        button2Action={confirmBulkDelete}
        button1={CANCEL_TEXT}
        button2={isSaving ? 'Deleting...' : 'Delete Records'}
        cancelButtonId="ai-tool-mis-bulk-delete-cancel"
        submitButtonId="ai-tool-mis-bulk-delete-confirm"
        button2Disabled={isSaving || selectedRequestIdList.length === 0}
      />}
    </section>
  );
};

export default AIToolMIS;
